import AppPageCarded from "@app-core/AppPageCarded/AppPageCarded"
import {ErrorModal} from "@app-core/ErrorModal"
import {LoadingScreen} from "@app-core/LoadingScreen"
import {useAuth} from "auth/AuthContext"
import {AuriPlaceholder} from "components/AuriPlaceholder"
import {
  DTC_STATUS_DENIED,
  EligibilityAppProcessSelection
} from "components/EligibilityAppProcessSelection"
import {
  APPLICATION_STATUS_YES_NEEDS_DOCTOR,
  EligibilityApplicationStatus
} from "components/EligibilityApplicationStatus"
import {EligibilityPractitioner} from "components/EligibilityPractitioner"
import {EligibilityQuestion} from "components/EligibilityQuestion"
import {EligibilitySoftExit} from "components/EligibilitySoftExit"
import type {SoftExitContact} from "components/EligibilitySoftExit"
import {EligibilityStartView} from "components/EligibilityStartView"
import {EligibilitySummary} from "components/EligibilitySummary"
import {EligibilityWelcomeMsg} from "components/EligibilityWelcomeMsg"
import {GHL_NAVIGATOR_BOOKING_URL} from "config/featureFlags"
import {eligibilityQuestionApi, leadApi} from "data/api"
import React, {useEffect, useState} from "react"
import {
  isCumulative,
  isImpairment
} from "data/constants/eligibilityQuestionCategories"
import {useNavigate} from "react-router-dom"
import {useEligibilityApplication} from "hooks/useEligibilityApplication"
import {useMessages} from "hooks/useMessages"
import {IEligibilityApplication} from "models/IEligibilityApplication"
import {IEligibilityOutcome} from "models/IEligibilityOutcome"
import {IEligibilityQuestion} from "models/IEligibilityQuestion"
import {IEligibilityQuestionAnswer} from "models/IEligibilityQuestionAnswer"
import {
  EligibilityEvaluation,
  EligibilityOutcome,
  evaluateEligibility
} from "utils/evaluateEligibility"

// Ordered steps of the reworked Benefits Check flow (brief Screens 2-6 + results).
enum Step {
  DtcStatus = "DTC_STATUS", // Screen 2
  ApplicationStatus = "APPLICATION_STATUS", // Screen 3
  Practitioner = "PRACTITIONER", // Screen 4
  Overview = "OVERVIEW", // Screen 5
  Transition = "TRANSITION", // Screen 6
  Questions = "QUESTIONS", // Screens 7-16
  Summary = "SUMMARY", // positive result
  SoftExit = "SOFT_EXIT", // all-no result
  Denied = "DENIED", // Screen 2 "denied" -> Auri denial flow
  Pathway3 = "PATHWAY3" // Screen 3 "yes, needs doctor" -> records-first review
}

export const EligibilityView: React.FC = () => {
  const navigate = useNavigate()
  const {user} = useAuth()
  const {addMessage} = useMessages()
  const {setEligibilityApplication, setEligibilityOutcome} =
    useEligibilityApplication()

  const [eligibilityQuestions, setEligibilityQuestions] = useState<
    IEligibilityQuestion[] | null
  >()
  const [step, setStep] = useState<Step>(Step.DtcStatus)
  const [appProcess, setAppProcess] = useState<string>("") // Screen 2
  const [applicationStatus, setApplicationStatus] = useState<string>("") // Screen 3
  const [practitioner, setPractitioner] = useState<string>("") // Screen 4
  const [currentQuestion, setCurrentQuestion] =
    useState<IEligibilityQuestion | null>(null)
  const [answers, setAnswers] = useState<IEligibilityQuestionAnswer[]>([])
  const [evaluation, setEvaluation] = useState<EligibilityEvaluation | null>(
    null
  )
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    eligibilityQuestionApi
      .fetchEligibilityQuestions()
      .then(setEligibilityQuestions)
      .catch(() => setEligibilityQuestions(null))
  }, [])

  const toEvalAnswers = (selected: IEligibilityQuestionAnswer[]) =>
    selected.map((a) => ({type: a.question.type, answer: a.answer}))

  // Question sequence: impairment questions (API order) then the Cumulative
  // Effects (Q10) question. Q10 is shown to EVERYONE now (brief decision #11);
  // the practitioner question is no longer here (it moved to Screen 4). So the
  // sequence is a constant 10 -> "Question X of 10".
  const buildSequence = (
    questions: IEligibilityQuestion[]
  ): IEligibilityQuestion[] => {
    const impairment = questions.filter((q) => isImpairment(q.type))
    const cumulative = questions.find((q) => isCumulative(q.type))
    return cumulative ? [...impairment, cumulative] : [...impairment]
  }

  const onSelectAnswer = (answer: string) => {
    if (!currentQuestion) {
      return
    }
    const selectedAnswers = [...answers]
    const answerIndex = selectedAnswers.findIndex(
      (q) => q.question.id === currentQuestion.id
    )
    if (answerIndex < 0) {
      selectedAnswers.push({question: currentQuestion, answer})
    } else {
      selectedAnswers[answerIndex].answer = answer
    }
    setAnswers(selectedAnswers)
  }

  const getAnswer = (): string => {
    if (!currentQuestion) {
      return ""
    }
    const answerIndex = answers.findIndex(
      (q) => q.question.id === currentQuestion.id
    )
    return answerIndex < 0 ? "" : answers[answerIndex].answer
  }

  // Stores the outcome in context and posts the lead to dtc-api-service. The
  // post is best-effort: a missing/failing endpoint must never block the flow.
  const recordOutcome = async (outcome: IEligibilityOutcome) => {
    setEligibilityOutcome(outcome)
    try {
      await leadApi.submitEligibilityOutcome(outcome)
    } catch {
      // Endpoint not yet available (cross-repo step) or transient failure.
    }
  }

  // ----- Step transitions -------------------------------------------------

  const onDtcStatusContinue = () => {
    // Denied applicants go to the Auri denial flow and do NOT continue to the
    // eligibility questions.
    if (appProcess === DTC_STATUS_DENIED) {
      setStep(Step.Denied)
      return
    }
    setStep(Step.ApplicationStatus)
  }

  const onApplicationStatusContinue = () => {
    // Already-prepared applications needing a signature go to the records-first
    // review gate (Pathway 3). Otherwise continue to the practitioner screen.
    if (applicationStatus === APPLICATION_STATUS_YES_NEEDS_DOCTOR) {
      setStep(Step.Pathway3)
      return
    }
    setStep(Step.Practitioner)
  }

  const onStartQuestions = () => {
    setCurrentQuestion(sequence[0] ?? null)
    setStep(Step.Questions)
  }

  const finalizeAssessment = () => {
    if (!sequence.every((q) => answers.some((a) => a.question.id === q.id))) {
      addMessage({severity: "error", content: "Please answer all questions"})
      return
    }
    const result = evaluateEligibility(toEvalAnswers(answers))
    setEvaluation(result)
    setStep(
      result.outcome === EligibilityOutcome.Qualified
        ? Step.Summary
        : Step.SoftExit
    )
  }

  const onQuestionNext = () => {
    if (!currentQuestion) {
      return
    }
    const currentIndex = sequence.findIndex((q) => q.id === currentQuestion.id)
    const nextIndex = currentIndex + 1
    if (nextIndex >= sequence.length) {
      finalizeAssessment()
      return
    }
    setCurrentQuestion(sequence[nextIndex])
  }

  const onQuestionBack = () => {
    if (!currentQuestion) {
      return
    }
    const currentIndex = sequence.findIndex((q) => q.id === currentQuestion.id)
    if (currentIndex <= 0) {
      setStep(Step.Transition)
      return
    }
    setCurrentQuestion(sequence[currentIndex - 1])
  }

  const onBackToQuestions = () => {
    setStep(Step.Questions)
  }

  const onConfirmPayment = async () => {
    if (submitting) {
      return
    }
    // Hard guard: only a QUALIFIED outcome may proceed to plan/payment.
    if (!evaluation || evaluation.outcome !== EligibilityOutcome.Qualified) {
      addMessage({
        severity: "error",
        content: "This application is not eligible to continue to payment."
      })
      return
    }

    // Only impairment sections feed the DTC application/payment pipeline.
    const diagnoses = answers
      .filter((a) => a.answer === "Yes" && isImpairment(a.question.type))
      .map((q) => q.question.type)

    const dtcApp: IEligibilityApplication = {
      diagnoses,
      plan: "",
      applicationProcess: appProcess,
      // The legal-expert representative checkbox was removed; keep the field
      // defaulted false in the payload (brief decision #5).
      allowRepresentativeContact: false
    }

    setSubmitting(true)
    await recordOutcome({
      outcome: EligibilityOutcome.Qualified,
      cumulativeCaseFlag: evaluation.cumulativeCaseFlag,
      navigatorReviewRequested: false
    })

    setEligibilityApplication(dtcApp)
    if (user) {
      navigate("/plan")
    } else {
      navigate("/sign-in", {state: {redirectUrl: "/plan"}})
    }
  }

  const onSoftExitSubmit = async (contact: SoftExitContact) => {
    if (submitting) {
      return
    }
    setSubmitting(true)
    await recordOutcome({
      outcome: EligibilityOutcome.SoftExit,
      cumulativeCaseFlag: evaluation?.cumulativeCaseFlag ?? false,
      navigatorReviewRequested: contact.navigatorReviewRequested,
      name: contact.name,
      email: contact.email,
      phone: contact.phone
    })
    // Decision #7: capture the lead first, then send navigator requests to the
    // GHL booking calendar (when the URL is configured). Everyone else returns
    // home with a confirmation.
    if (contact.navigatorReviewRequested && GHL_NAVIGATOR_BOOKING_URL) {
      window.location.href = GHL_NAVIGATOR_BOOKING_URL
      return
    }
    addMessage({
      severity: "success",
      content: contact.navigatorReviewRequested
        ? "Thank you. A Benefits2 navigator will be in touch."
        : "Thank you. We've recorded your response."
    })
    navigate("/")
  }

  if (eligibilityQuestions === null)
    return <ErrorModal error={`Issue Loading Eligibility Questions`} />
  if (eligibilityQuestions === undefined)
    return <LoadingScreen isLoading={true} />

  const sequence = buildSequence(eligibilityQuestions)
  const questionNum = currentQuestion
    ? sequence.findIndex((q) => q.id === currentQuestion.id) + 1
    : 0

  const renderStep = () => {
    switch (step) {
      case Step.DtcStatus:
        return (
          <EligibilityAppProcessSelection
            selectedProcess={appProcess}
            onSelectedProcess={setAppProcess}
            onContinue={onDtcStatusContinue}
            onBack={() => navigate("/")}
          />
        )
      case Step.ApplicationStatus:
        return (
          <EligibilityApplicationStatus
            value={applicationStatus}
            onSelect={setApplicationStatus}
            onContinue={onApplicationStatusContinue}
            onBack={() => setStep(Step.DtcStatus)}
          />
        )
      case Step.Practitioner:
        return (
          <EligibilityPractitioner
            value={practitioner}
            onSelect={setPractitioner}
            onContinue={() => setStep(Step.Overview)}
            onBack={() => setStep(Step.ApplicationStatus)}
          />
        )
      case Step.Overview:
        return <EligibilityWelcomeMsg onNext={() => setStep(Step.Transition)} />
      case Step.Transition:
        return <EligibilityStartView onStart={onStartQuestions} />
      case Step.Questions:
        return currentQuestion ? (
          <EligibilityQuestion
            question={currentQuestion}
            questionNum={questionNum}
            answer={getAnswer()}
            totalQuestions={sequence.length}
            onSelectAnswer={onSelectAnswer}
            onNext={onQuestionNext}
            onBack={onQuestionBack}
          />
        ) : null
      case Step.Summary:
        return (
          <EligibilitySummary
            onBack={onBackToQuestions}
            onConfirmClick={onConfirmPayment}
            answers={answers.filter(
              (a) => a.answer === "Yes" && isImpairment(a.question.type)
            )}
          />
        )
      case Step.SoftExit:
        return (
          <EligibilitySoftExit
            onSubmit={onSoftExitSubmit}
            onBack={onBackToQuestions}
            isSubmitting={submitting}
          />
        )
      case Step.Denied:
        // Auri denial flow (external). Until Auri is integrated, hand off.
        return (
          <AuriPlaceholder
            title="Let's help with your previous denial."
            message="Because your Disability Tax Credit application was previously denied, a Benefits2 specialist will review your Notice of Determination and guide your next steps. You don't need to complete the eligibility questions — we'll be in touch."
            onBack={() => setStep(Step.DtcStatus)}
          />
        )
      case Step.Pathway3:
        // Records-first review gate ($99). Dedicated screen + Stripe product is
        // a follow-up; hand off for now so the path doesn't dead-end.
        return (
          <AuriPlaceholder
            title="Let's review your prepared application."
            message="Since your application is already prepared and needs a doctor's signature, a Benefits2 specialist will complete a records-first review before you continue. We'll be in touch about the next steps."
            onBack={() => setStep(Step.ApplicationStatus)}
          />
        )
      default:
        return null
    }
  }

  return <AppPageCarded scroll="page">{renderStep()}</AppPageCarded>
}

export default EligibilityView
